import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { apiFetch } from '../utils/api';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  Star,
  Calendar,
  Package,
  Store,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  X,
  Trash2,
  Download,
  ArrowUpDown,
  Brain,
  Link2,
} from 'lucide-react';

const sentiments = [
  { value: 'all', label: 'All Sentiments' },
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
];

const ratingOptions = [
  { value: 'all', label: 'All Ratings' },
  { value: '5', label: '5 Stars' },
  { value: '4', label: '4 Stars' },
  { value: '3', label: '3 Stars' },
  { value: '2', label: '2 Stars' },
  { value: '1', label: '1 Star' },
];

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'highest_rating', label: 'Highest Rating' },
  { value: 'lowest_rating', label: 'Lowest Rating' },
];

export default function ReviewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  // Initialise pagination from URL so shared links keep their page number
  const [pagination, setPagination] = useState(() => ({
    page: parseInt(searchParams.get('page') || '1', 10) || 1,
    pages: 1,
    total: 0,
  }));
  const [stats, setStats] = useState({ avg_rating: 0, positive: 0, negative: 0, neutral: 0, analyzed: 0 });

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Dynamic lists from API
  const [brandList, setBrandList] = useState([]);
  const [platformList, setPlatformList] = useState([]);
  const [brandLogos, setBrandLogos] = useState({});
  const [platformIcons, setPlatformIcons] = useState({});
  const [productImages, setProductImages] = useState({});

  // URL filter params (inbound — shareable deep-links)
  const highlightTerm = searchParams.get('highlight') || '';
  const filterTopic = searchParams.get('topic') || '';
  const filterKeyword = searchParams.get('keyword') || '';
  const filterFeature = searchParams.get('feature') || '';
  const filterBrandParam = searchParams.get('brand') || '';
  const filterPlatformParam = searchParams.get('platform') || '';
  const filterProductParam = searchParams.get('product') || '';
  const filterSentimentParam = searchParams.get('sentiment') || '';
  const qParam = searchParams.get('q') || '';
  const ratingParam = searchParams.get('rating') || '';
  const sortParam = searchParams.get('sort') || '';
  const fromParam = searchParams.get('from') || '';
  const toParam = searchParams.get('to') || '';

  // Filters — initialised from URL so sharing a link opens the same view
  const [search, setSearch] = useState(qParam || highlightTerm || filterTopic || filterKeyword || filterFeature || '');
  const [platform, setPlatform] = useState(filterPlatformParam || 'all');
  const [brand, setBrand] = useState(filterBrandParam || 'all');
  const [sentiment, setSentiment] = useState(filterSentimentParam || 'all');
  const [rating, setRating] = useState(ratingParam || 'all');
  const [product, setProduct] = useState(filterProductParam || '');
  const [sortBy, setSortBy] = useState(sortParam || 'newest');
  const [dateFrom, setDateFrom] = useState(fromParam);
  const [dateTo, setDateTo] = useState(toParam);
  const [exporting, setExporting] = useState(false);

  const hasActiveFilters = highlightTerm || filterTopic || filterKeyword || filterFeature;

  useEffect(() => {
    const term = highlightTerm || filterTopic || filterKeyword || filterFeature;
    if (term) setSearch(term);
    if (filterBrandParam) setBrand(filterBrandParam);
    if (filterPlatformParam) setPlatform(filterPlatformParam);
    if (filterSentimentParam) setSentiment(filterSentimentParam);
    if (filterProductParam) setProduct(filterProductParam);
  }, [searchParams]);

  useEffect(() => {
    fetchReviews();
  }, [platform, brand, sentiment, rating, pagination.page, search, product, sortBy, dateFrom, dateTo]);

  // Keep the URL in sync with filter state so views are shareable. Debounced
  // so typing in the search box doesn't thrash the history stack.
  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      const set = (k, v) => {
        if (v !== undefined && v !== null && v !== '' && v !== 'all') params.set(k, v);
        else params.delete(k);
      };
      set('q', search);
      set('platform', platform);
      set('brand', brand);
      set('sentiment', sentiment);
      set('rating', rating);
      set('product', product);
      set('sort', sortBy !== 'newest' ? sortBy : '');
      set('from', dateFrom);
      set('to', dateTo);
      if (pagination.page > 1) params.set('page', String(pagination.page));
      else params.delete('page');
      // Collapse legacy inbound-only params into `q` once consumed
      if (search) {
        ['highlight', 'topic', 'keyword', 'feature'].forEach((k) => params.delete(k));
      }
      const next = params.toString();
      const curr = searchParams.toString();
      if (next !== curr) setSearchParams(params, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, platform, brand, sentiment, rating, product, sortBy, dateFrom, dateTo, pagination.page]);

  const copyShareableLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Shareable link copied to clipboard');
    } catch (_) {
      toast.error('Could not copy link');
    }
  };

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [bRes, pRes, imgRes] = await Promise.all([
          apiFetch('/api/brands', { credentials: 'include' }),
          apiFetch('/api/platforms', { credentials: 'include' }),
          apiFetch('/api/products/grouped', { credentials: 'include' }),
        ]);
        if (bRes.ok) {
          const bl = await bRes.json();
          setBrandList(bl);
          const logos = {};
          bl.forEach(b => { if (b.logo_url) logos[b.name] = b.logo_url; });
          setBrandLogos(logos);
        }
        if (pRes.ok) {
          const pl = await pRes.json();
          setPlatformList(pl);
          const icons = {};
          pl.forEach(p => { if (p.icon_url) icons[p.name] = p.icon_url; });
          setPlatformIcons(icons);
        }
        if (imgRes.ok) {
          const grouped = await imgRes.json();
          const imgMap = {};
          (grouped.brands || []).forEach(b => {
            b.products.forEach(p => {
              if (p.image_url) imgMap[p.sku_name] = p.image_url;
            });
          });
          setProductImages(imgMap);
        }
      } catch (err) { console.error(err); }
    };
    fetchMeta();
  }, []);

  const highlightText = (text, term) => {
    if (!term || !text) return text;
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded font-semibold">{part}</mark>
      ) : part
    );
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearch('');
    setPlatform('all');
    setBrand('all');
    setSentiment('all');
    setRating('all');
    setProduct('');
    setSortBy('newest');
    setDateFrom('');
    setDateTo('');
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', '20');
      params.append('sort_by', sortBy);
      if (platform && platform !== 'all') params.append('platform', platform);
      if (brand && brand !== 'all') params.append('brand', brand);
      if (sentiment && sentiment !== 'all') params.append('sentiment', sentiment);
      if (rating && rating !== 'all') params.append('rating', rating);
      if (search) params.append('search', search);
      if (product) params.append('product', product);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await apiFetch(`/api/reviews?${params}`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews);
        setPagination({ page: data.page, pages: data.pages, total: data.total });
        if (data.stats) setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchReviews();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (platform && platform !== 'all') params.append('platform', platform);
      if (brand && brand !== 'all') params.append('brand', brand);
      if (sentiment && sentiment !== 'all') params.append('sentiment', sentiment);
      if (rating && rating !== 'all') params.append('rating', rating);
      if (search) params.append('search', search);
      if (product) params.append('product', product);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await apiFetch(`/api/reviews/export?${params}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'reviews_export.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) { console.error(err); }
    finally { setExporting(false); }
  };

  const toggleSelectAll = () => {
    if (selectAllMatching || selectedIds.length === reviews.length) {
      setSelectedIds([]);
      setSelectAllMatching(false);
    } else {
      setSelectedIds(reviews.map(r => r.review_id));
    }
  };

  const selectAllMatchingReviews = () => {
    setSelectAllMatching(true);
    setSelectedIds(reviews.map(r => r.review_id)); // keep page checkboxes visually ticked too
  };

  const toggleSelectReview = (reviewId) => {
    setSelectAllMatching(false);
    setSelectedIds(prev =>
      prev.includes(reviewId) ? prev.filter(id => id !== reviewId) : [...prev, reviewId]
    );
  };

  // Build the same filter query-string used by GET /api/reviews
  const buildFilterParams = () => {
    const p = new URLSearchParams();
    if (platform !== 'all') p.append('platform', platform);
    if (brand !== 'all') p.append('brand', brand);
    if (sentiment !== 'all') p.append('sentiment', sentiment);
    if (rating !== 'all') p.append('rating', rating);
    if (search) p.append('search', search);
    if (product) p.append('product', product);
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    return p;
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try {
      let response;
      if (selectAllMatching) {
        // Delete all matching reviews across all pages (filter-aware)
        const qs = buildFilterParams().toString();
        response = await apiFetch(`/api/reviews/filtered${qs ? '?' + qs : ''}`, { method: 'DELETE' });
      } else if (selectedIds.length > 0) {
        response = await apiFetch('/api/reviews/bulk', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(selectedIds),
        });
      } else {
        return;
      }
      if (response && response.ok) {
        setSelectedIds([]);
        setSelectAllMatching(false);
        setShowDeleteDialog(false);
        fetchReviews();
      }
    } catch (error) { console.error(error); }
    finally { setDeleting(false); }
  };

  const getSentimentColor = (s) => {
    switch (s) {
      case 'positive': return 'bg-[#2eb4ac]/10 text-[#2eb4ac]';
      case 'negative': return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    } catch { return 'N/A'; }
  };

  const positivePercent = pagination.total > 0 ? Math.round((stats.positive / pagination.total) * 100) : 0;
  const negativePercent = pagination.total > 0 ? Math.round((stats.negative / pagination.total) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reviews-page">
      {/* Breadcrumb */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 text-sm">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-foreground p-1">Dashboard</Button>
          {filterBrandParam && (<><span className="text-muted-foreground">/</span><Button variant="ghost" size="sm" onClick={() => navigate(`/brand/${encodeURIComponent(filterBrandParam)}`)} className="text-muted-foreground hover:text-foreground p-1">{filterBrandParam}</Button></>)}
          {filterPlatformParam && filterBrandParam && (<><span className="text-muted-foreground">/</span><Button variant="ghost" size="sm" onClick={() => navigate(`/brand/${encodeURIComponent(filterBrandParam)}/channel/${encodeURIComponent(filterPlatformParam)}`)} className="text-muted-foreground hover:text-foreground p-1">{filterPlatformParam}</Button></>)}
          {filterProductParam && filterBrandParam && filterPlatformParam && (<><span className="text-muted-foreground">/</span><Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground p-1 truncate max-w-[150px]">{filterProductParam}</Button></>)}
          <span className="text-muted-foreground">/</span>
          <span className="text-foreground font-medium">Reviews</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Reviews</h1>
          <p className="text-muted-foreground mt-1">Unified review inbox from all platforms</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="gap-1.5" data-testid="export-reviews-btn">
          <Download className="w-3.5 h-3.5" />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>

      {/* Active Filter Banner */}
      {hasActiveFilters && (
        <Card className="border-[#8b5cf6]/30 bg-[#8b5cf6]/5">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Sparkles className="w-4 h-4 text-[#8b5cf6]" />
              <span className="text-sm">Showing reviews containing: <mark className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded font-semibold">{search}</mark></span>
              {filterBrandParam && <Badge variant="secondary">{filterBrandParam}</Badge>}
              {filterPlatformParam && <Badge variant="secondary">{filterPlatformParam}</Badge>}
              {filterProductParam && <Badge variant="secondary" className="truncate max-w-[150px]">{filterProductParam}</Badge>}
              {filterSentimentParam && <Badge variant="secondary" className="capitalize">{filterSentimentParam}</Badge>}
            </div>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4 mr-1" /> Clear</Button>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats Bar */}
      {!loading && pagination.total > 0 && (
        <div className="flex items-center gap-6 px-4 py-2.5 rounded-lg bg-accent/30 border border-border" data-testid="reviews-stats-bar">
          <span className="text-sm font-semibold">{pagination.total.toLocaleString()} reviews</span>
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Star className="w-3 h-3 text-[#f59e0b] fill-[#f59e0b]" /> {stats.avg_rating} avg
          </span>
          <span className="text-sm text-[#2eb4ac]">{positivePercent}% positive</span>
          <span className="text-sm text-red-400">{negativePercent}% negative</span>
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Brain className="w-3 h-3" /> {stats.analyzed} analyzed
          </span>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Filters sidebar */}
        <Card className="lg:w-64 flex-shrink-0" data-testid="reviews-filters">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Filter className="w-4 h-4" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Platform</label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="filter-platform"><SelectValue placeholder="All Platforms" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {platformList.map((p) => (
                    <SelectItem key={p.platform_id || p.name} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Brand</label>
              <Select value={brand} onValueChange={setBrand}>
                <SelectTrigger data-testid="filter-brand"><SelectValue placeholder="All Brands" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brandList.map((b) => (
                    <SelectItem key={b.brand_id || b.name} value={b.name}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Product / SKU</label>
              <Input
                placeholder="Filter by product..."
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                className="text-sm"
                data-testid="filter-product"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Sentiment</label>
              <Select value={sentiment} onValueChange={setSentiment}>
                <SelectTrigger data-testid="filter-sentiment"><SelectValue placeholder="All Sentiments" /></SelectTrigger>
                <SelectContent>
                  {sentiments.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Rating</label>
              <Select value={rating} onValueChange={setRating}>
                <SelectTrigger data-testid="filter-rating"><SelectValue placeholder="All Ratings" /></SelectTrigger>
                <SelectContent>
                  {ratingOptions.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Date Range</label>
              <div className="space-y-2">
                <Input type="month" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="text-xs" data-testid="filter-date-from" />
                <Input type="month" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="text-xs" data-testid="filter-date-to" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Sort By</label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="filter-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sortOptions.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" className="w-full" onClick={clearFilters} data-testid="clear-filters-btn">
              Clear Filters
            </Button>
          </CardContent>
        </Card>

        {/* Main content */}
        <div className="flex-1 space-y-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search reviews by text or product..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-reviews-input" />
            </div>
            <Button type="submit" data-testid="search-reviews-btn">Search</Button>
            <Button
              type="button"
              variant="outline"
              onClick={copyShareableLink}
              title="Copy a shareable link that opens this exact filtered view"
              data-testid="copy-share-link-btn"
            >
              <Link2 className="w-4 h-4 mr-1.5" />
              Share link
            </Button>
          </form>

          {/* Results count & Bulk Actions */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4 flex-wrap">
              <p className="text-sm text-muted-foreground">{pagination.total} reviews found</p>
              {reviews.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Checkbox
                    id="select-all"
                    checked={selectAllMatching || (selectedIds.length === reviews.length && reviews.length > 0)}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-page-checkbox"
                  />
                  <label htmlFor="select-all" className="text-sm text-muted-foreground cursor-pointer">
                    {selectAllMatching
                      ? `All ${pagination.total.toLocaleString()} matching selected`
                      : selectedIds.length === reviews.length && reviews.length > 0
                        ? `${reviews.length} on this page selected`
                        : 'Select all on page'}
                  </label>
                  {/* Inline one-click "Select all N matching" action */}
                  {pagination.total > reviews.length && !selectAllMatching && (
                    <button
                      type="button"
                      onClick={selectAllMatchingReviews}
                      className="text-xs font-medium text-[#8b5cf6] hover:underline"
                      data-testid="select-all-matching-btn"
                    >
                      · Select all {pagination.total.toLocaleString()} matching filters
                    </button>
                  )}
                  {selectAllMatching && (
                    <button
                      type="button"
                      onClick={() => { setSelectAllMatching(false); setSelectedIds([]); }}
                      className="text-xs font-medium text-[#8b5cf6] hover:underline"
                      data-testid="clear-select-all-matching-btn"
                    >
                      · Clear selection
                    </button>
                  )}
                </div>
              )}
            </div>
            {(selectedIds.length > 0 || selectAllMatching) && (
              <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)} className="gap-2" data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4" />
                {selectAllMatching
                  ? `Delete all ${pagination.total.toLocaleString()} matching`
                  : `Delete ${selectedIds.length} selected`}
              </Button>
            )}
          </div>

          {/* Reviews list */}
          <ScrollArea className="h-[calc(100vh-380px)]">
            <div className="space-y-3 pr-4">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <Card key={i}><CardContent className="p-4"><Skeleton className="h-4 w-48 mb-2" /><Skeleton className="h-3 w-full mb-2" /><Skeleton className="h-3 w-3/4" /></CardContent></Card>
                ))
              ) : reviews.length > 0 ? (
                reviews.map((review) => (
                  <Card key={review.review_id} className={`card-hover cursor-pointer ${selectedIds.includes(review.review_id) ? 'ring-2 ring-[#8b5cf6]' : ''}`} data-testid={`review-card-${review.review_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selectedIds.includes(review.review_id)} onCheckedChange={() => toggleSelectReview(review.review_id)} />
                        </div>
                        <div className="flex-1 min-w-0" onClick={() => setSelectedReview(review)}>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <div className="flex items-center gap-1">
                              {Array(5).fill(0).map((_, i) => (
                                <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-[#f59e0b] fill-[#f59e0b]' : 'text-muted-foreground'}`} />
                              ))}
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {platformIcons[review.platform] ? <img src={platformIcons[review.platform]} alt="" className="w-3 h-3 rounded object-cover mr-1" /> : <Store className="w-3 h-3 mr-1" />}
                              {review.platform}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {brandLogos[review.brand] ? <img src={brandLogos[review.brand]} alt="" className="w-3 h-3 rounded object-cover mr-1" /> : null}
                              {review.brand}
                            </Badge>
                            {review.analyzed && (
                              <Badge variant="secondary" className="text-[10px] bg-[#2eb4ac]/10 text-[#2eb4ac] border-[#2eb4ac]/20 gap-0.5" data-testid="ai-analyzed-badge">
                                <Brain className="w-2.5 h-2.5" /> AI
                              </Badge>
                            )}
                          </div>

                          <div className="text-sm font-medium mb-1 flex items-center gap-2">
                            <div className="w-6 h-6 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                              {productImages[review.product_name] ? (
                                <img src={productImages[review.product_name]} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : (
                                <Package className="w-3 h-3 text-muted-foreground" />
                              )}
                            </div>
                            {review.product_name}
                          </div>

                          <p className="text-sm text-muted-foreground line-clamp-3">
                            {highlightText(review.review_text, search)}
                          </p>

                          <div className="flex items-center gap-2 mt-3 flex-wrap">
                            {review.sentiment && (
                              <Badge className={getSentimentColor(review.sentiment)}>{review.sentiment}</Badge>
                            )}
                            {review.detected_issues?.slice(0, 2).map((issue) => (
                              <Badge key={issue} variant="secondary" className="text-xs">{issue}</Badge>
                            ))}
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(review.review_date)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card><CardContent className="p-8 text-center"><p className="text-muted-foreground">No reviews found. Upload reviews to get started.</p></CardContent></Card>
              )}
            </div>
          </ScrollArea>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={pagination.page === 1} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))} data-testid="prev-page-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">Page {pagination.page} of {pagination.pages}</span>
              <Button variant="outline" size="sm" disabled={pagination.page === pagination.pages} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))} data-testid="next-page-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Review detail dialog */}
      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="max-w-2xl" data-testid="review-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Review Details</DialogTitle>
            <DialogDescription>AI-analyzed review from {selectedReview?.platform}</DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {Array(5).fill(0).map((_, i) => (
                  <Star key={i} className={`w-5 h-5 ${i < selectedReview.rating ? 'text-[#f59e0b] fill-[#f59e0b]' : 'text-muted-foreground'}`} />
                ))}
                <span className="text-sm text-muted-foreground ml-2">{formatDate(selectedReview.review_date)}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">
                  {platformIcons[selectedReview.platform] ? <img src={platformIcons[selectedReview.platform]} alt="" className="w-3.5 h-3.5 rounded object-cover mr-1" /> : <Store className="w-3 h-3 mr-1" />}
                  {selectedReview.platform}
                </Badge>
                <Badge variant="outline">
                  {brandLogos[selectedReview.brand] ? <img src={brandLogos[selectedReview.brand]} alt="" className="w-3.5 h-3.5 rounded object-cover mr-1" /> : null}
                  {selectedReview.brand}
                </Badge>
                <Badge variant="outline">
                  <Package className="w-3 h-3 mr-1" /> {selectedReview.product_name}
                </Badge>
              </div>
              <Card><CardContent className="p-4"><p className="text-sm leading-relaxed">{highlightText(selectedReview.review_text, search)}</p></CardContent></Card>
              {selectedReview.analyzed && (
                <Card className="bg-[#2eb4ac]/5 border-[#2eb4ac]/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#2eb4ac]" /> AI Analysis</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Sentiment:</span>
                      <Badge className={getSentimentColor(selectedReview.sentiment)}>{selectedReview.sentiment}</Badge>
                      {selectedReview.sentiment_score && <span className="text-xs text-muted-foreground">({(selectedReview.sentiment_score * 100).toFixed(0)}% confidence)</span>}
                    </div>
                    {selectedReview.detected_issues?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium">Detected Issues:</span>
                        <div className="flex flex-wrap gap-1 mt-1">{selectedReview.detected_issues.map((issue) => (<Badge key={issue} variant="secondary" className="text-xs">{issue}</Badge>))}</div>
                      </div>
                    )}
                    {selectedReview.keywords?.length > 0 && (
                      <div>
                        <span className="text-sm font-medium">Keywords:</span>
                        <div className="flex flex-wrap gap-1 mt-1">{selectedReview.keywords.map((kw) => (<Badge key={kw} variant="outline" className="text-xs">{kw}</Badge>))}</div>
                      </div>
                    )}
                    {selectedReview.ai_summary && (
                      <div>
                        <span className="text-sm font-medium">Summary:</span>
                        <p className="text-sm text-muted-foreground mt-1">{selectedReview.ai_summary}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectAllMatching ? pagination.total.toLocaleString() : selectedIds.length} review{(selectAllMatching ? pagination.total : selectedIds.length) !== 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectAllMatching
                ? `This will permanently delete ALL ${pagination.total.toLocaleString()} reviews matching your current filters — across every page. This action cannot be undone.`
                : 'This action cannot be undone. The selected reviews will be permanently deleted.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-bulk-delete-btn">
              {deleting
                ? 'Deleting...'
                : `Delete ${selectAllMatching ? pagination.total.toLocaleString() : selectedIds.length} review${(selectAllMatching ? pagination.total : selectedIds.length) !== 1 ? 's' : ''}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
